import { baseUrl, unlinkUrls } from 'urls'

/** Per-connection uninstall URLs for integrations backed by AWS Secrets Manager + integrations_keys. */
export const SM_INTEGRATION_UNINSTALL_URLS: Record<string, (connectionId: string) => string> = {
  google_analytics: (cid) => `${baseUrl}/auth/google-analytics/connections/${encodeURIComponent(cid)}`,
  google_tag_manager: (cid) => `${baseUrl}/auth/google-tag-manager/connections/${encodeURIComponent(cid)}`,
  google_merchant_center: (cid) =>
    `${baseUrl}/auth/google-merchant-center/connections/${encodeURIComponent(cid)}`,
  google_search_console: (cid) =>
    `${baseUrl}/auth/google-search-console/connections/${encodeURIComponent(cid)}`,
  google_marketing: (cid) =>
    `${baseUrl}/auth/google-marketing/connections/${encodeURIComponent(cid)}`,
  'amazon-ads': (cid) => `${baseUrl}/auth/amazon-ads/connections/${encodeURIComponent(cid)}`,
  pinterest: (cid) => `${baseUrl}/auth/pinterest/connections/${encodeURIComponent(cid)}`,
  tiktok: (cid) => `${baseUrl}/auth/tiktok/connections/${encodeURIComponent(cid)}`,
  linkedin: (cid) => `${baseUrl}/auth/linkedin/connections/${encodeURIComponent(cid)}`,
  xcom: (cid) => `${baseUrl}/auth/xcom/connections/${encodeURIComponent(cid)}`,
  xcom_ads: (cid) => `${baseUrl}/auth/xcom-ads/connections/${encodeURIComponent(cid)}`,
  klaviyo: (cid) => `${baseUrl}/auth/klaviyo/connections/${encodeURIComponent(cid)}`,
  omnisend: (cid) => `${baseUrl}/auth/omnisend/connections/${encodeURIComponent(cid)}`,
  feedonomics: (cid) => `${baseUrl}/auth/feedonomics/connections/${encodeURIComponent(cid)}`,
  openai_ads: (cid) => `${baseUrl}/auth/openai-ads/connections/${encodeURIComponent(cid)}`,
  makeswift: (cid) => `${baseUrl}/auth/makeswift/connections/${encodeURIComponent(cid)}`,
}

/** Shippo: DELETE with JSON body `{ merchant_id, channel }` or `{}` for legacy `{owner}--shippo`. */
export const shippoDisconnectUrl = `${baseUrl}/auth/shippo/disconnect`

/** Slack: DELETE — removes this user's slack_app row + slack_user_link rows. */
export const slackDisconnectUrl = `${baseUrl}/auth/slack/disconnect`

/** Teams: DELETE — removes this user's teams_app row + teams_user_link rows. */
export const teamsDisconnectUrl = `${baseUrl}/auth/teams/disconnect`

export function hasSmIntegrationUninstall(channelId: string | undefined | null): boolean {
  return Boolean(channelId && SM_INTEGRATION_UNINSTALL_URLS[channelId])
}

/**
 * DELETE segment for `{owner}--{integrationId}` or `{owner}--{integrationId}--{account}` keys.
 * Uses `default` when `keyId` is exactly `{owner}--{integrationId}` (legacy single-secret rows).
 */
export function connectionIdForSmKeysDisconnect(
  ppKey: { keyId?: string; publicKey?: string; accountId?: string } | null | undefined,
  ownerId: string | number | null | undefined,
  integrationId: string | undefined
): string | null {
  if (!integrationId) return null
  const oid = ownerId != null ? String(ownerId) : ''
  const keyId = ppKey?.keyId != null ? String(ppKey.keyId) : ''
  if (oid && keyId) {
    const prefix = `${oid}--${integrationId}`
    if (keyId === prefix) return 'default'
    if (keyId.startsWith(`${prefix}--`)) {
      const rest = keyId.slice(prefix.length + 2)
      if (rest) return rest
    }
    // The UI id uses an underscore while X Ads secrets use a hyphen.
    if (integrationId === 'xcom_ads') {
      const adsPrefix = `${oid}--xcom-ads`
      if (keyId === adsPrefix) return 'default'
      if (keyId.startsWith(`${adsPrefix}--`)) {
        const rest = keyId.slice(adsPrefix.length + 2)
        if (rest) return rest
      }
    }
  }
  const pub = ppKey?.publicKey != null ? String(ppKey.publicKey).trim() : ''
  const acct = ppKey?.accountId != null ? String(ppKey.accountId).trim() : ''
  return pub || acct || null
}

/** Walmart / ChannelAdvisor: existing unlink API via UninstallModal. */
export function isUnlinkModalIntegration(
  integration: { id?: string; uninstallEnabled?: boolean; unlinkIdCol?: string } | null | undefined
): boolean {
  const id = integration?.id
  if (!integration?.uninstallEnabled || !integration.unlinkIdCol || !id) return false
  return Object.prototype.hasOwnProperty.call(unlinkUrls, id)
}

/** Stripe keys table: reuse StripeUninstallModal from the card body. */
export function hasStripeKeysTableDisconnect(
  integration: { id?: string; uninstallEnabled?: boolean } | null | undefined
): boolean {
  return Boolean(integration?.id === 'stripe' && integration?.uninstallEnabled)
}

/** Account table row-level Disconnect (SM/Shippo unlink, or marketplace unlink modals). */
export function hasIntegrationTableDisconnectActions(
  channelId: string | undefined | null,
  integration?: { id?: string; uninstallEnabled?: boolean; unlinkIdCol?: string } | null
): boolean {
  if (!channelId) return false
  if (SM_INTEGRATION_UNINSTALL_URLS[channelId] || channelId === 'shippo') return true
  return isUnlinkModalIntegration(integration ?? { id: channelId })
}

export function unlinkIdentifierFromAccount(
  account: Record<string, unknown>,
  unlinkIdCol: string | undefined
): string {
  if (!unlinkIdCol) return ''
  const direct = account[unlinkIdCol]
  if (direct != null && String(direct).trim() !== '') return String(direct).trim()
  const mid = account.merchant_id
  return mid != null ? String(mid).trim() : ''
}
